import RichEditor from "./QuillEditor";
import { AiSuggestion, AiSuggestionCTA } from "./AiSuggestion";
import { useAppSelector } from "lib/redux/hooks";
import {
  selectAccessToken,
  selectIsPremiumUser,
  selectSubscriptionCreditCount,
  setAccessToken,
} from "lib/redux/uiSlice";
import PremiumSparkle from "public/assets/premium-sparkle.svg";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

interface RichEditorWithSuggestionsProps<K extends string, V extends string[]> {
  name: K;
  value: V;
  onChange: (field: K, value: V) => void;
  placeholder?: string;
  section: string;
}

const RichEditorWithSuggestions = <T extends string>({
  name,
  value,
  onChange,
  placeholder,
  section,
}: RichEditorWithSuggestionsProps<T, string[]>) => {
  const accessToken = useAppSelector(selectAccessToken);
  const credits = useAppSelector(selectSubscriptionCreditCount);
  const isPremiumUser = useAppSelector(selectIsPremiumUser);
  const dispatch = useDispatch();
  const [alert, setAlert] = useState<string | null>(null);
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    const localAccessToken = localStorage.getItem("access-token");
    if (localAccessToken && !accessToken) {
      dispatch(setAccessToken(localAccessToken));
    }
  }, [accessToken, dispatch]);

  useEffect(() => {
    if (alert) {
      setShowAlert(true);
      const timeout = setTimeout(() => {
        setShowAlert(false); // Start closing animation
        setTimeout(() => setAlert(null), 300); // Wait for animation to finish
      }, 3000); // Alert stays visible for 3 seconds
      return () => clearTimeout(timeout);
    }
  }, [alert]);

  return (
    <div className="col-span-full w-full">
      <RichEditor
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
      {alert && (
        <div
          className={`relative mt-2 w-full transform rounded-2xl bg-gradient-to-r from-[#CD81AD]/100 to-[#FF9399]/100 py-3 text-center font-montserrat text-[10px] font-semibold text-white transition-all 
      duration-300 ease-out md:py-5 md:text-sm ${
        showAlert
          ? "translate-y-0 scale-100 opacity-100"
          : "translate-y-5 scale-90 opacity-0"
      }`}
        >
          {isPremiumUser && (
            <PremiumSparkle className="absolute left-2 top-2 inline-block h-4 w-4" />
          )}
          {alert}
        </div>
      )}
      <div className="mt-2 bg-white rounded-3xl">
        <AiSuggestion
          section={section}
          description={value}
          onChange={onChange}
          setAlert={setAlert}
        />
        {accessToken && credits === 0 && !isPremiumUser && <AiSuggestionCTA />}
      </div>
    </div>
  );
};

export default RichEditorWithSuggestions;
